import React from "react";
import { Flex, Text, Tooltip } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { EstimatedSupplyBreakdown } from "@/features/order-sheet-sets/utils/order-sheet-market-product";

interface FormulaProps {
  breakdown: EstimatedSupplyBreakdown;
}

const formatRate = (rate: number) =>
  rate.toLocaleString("en-US", { maximumFractionDigits: 2 });

// 0.05 → "5", 0.025 → "2.5", 0.370408 → "37.04" — 끝의 0 은 떼고 보여준다.
const formatPercent = (uplift: number) =>
  (uplift * 100).toFixed(2).replace(/\.?0+$/, "");

// 서버가 주는 항목 이름 → 번역 키. 모르는 이름이 오면(서버에 항목이 새로 생긴 경우) 묶음 이름으로 떨어진다.
const FACTOR_LABEL_KEY: Record<string, string> = {
  shipping: "estimated_supply_formula.factor_shipping",
  duty: "estimated_supply_formula.factor_duty",
  vat: "estimated_supply_formula.factor_vat",
  uno_fee: "estimated_supply_formula.factor_uno_fee",
};

/**
 * 예상 매입가가 어떻게 나왔는지 — 실제 숫자를 넣은 계산식.
 *   공급가 × (1 + 운송비) × (1 + 관세) × (1 + 부가세) × (1 + 우노 수수료) × 환율
 *     = 예상 매입가(천원 단위 반올림)
 * 비율 항목은 "× (1 + 13%)" 가 아니라 "13%" 로만 적는다 — 읽는 사람에게 필요한 건 몇 퍼센트가
 * 붙느냐이고, 곱셈 표기는 줄만 길게 만든다(각 비율은 앞 값에 차례로 얹힌다).
 * 부대비용은 뭉쳐 보여주지 않고 항목별로 한 줄씩 푼다. 항목은 서버가 준 구성을 그대로 쓴다 —
 * 우노 수수료는 바이어마다 다르고(부가세 10% 가 얹힌 값으로 온다 — 5% 면 5.5%), 관세는 분류·원산지에
 * 따라 다르며, 스탁오더가 아닌 세트에는
 * 운송비·부가세가 없다. 패스픽 핫딜 식에 있는 발주 수수료와 정액 수수료(통관수수료·국내배송비·
 * 박스비)는 이 식에 없다.
 * 계산은 getEstimatedSupplyBreakdown 이 하고, 여기서는 그 재료를 보여주기만 한다.
 */
export function EstimatedSupplyFormula({ breakdown }: FormulaProps) {
  const { t, tCurrencyString } = useI18nHelper();

  const rows: { key: string; label: string; value: string }[] = [
    {
      key: "supply",
      label: t("estimated_supply_formula.supply"),
      value: tCurrencyString(breakdown.amount, {
        type: "SYMBOL",
        code: breakdown.currency,
      }),
    },
  ];
  if (breakdown.upliftFactors.length > 0) {
    breakdown.upliftFactors.forEach((factor) => {
      rows.push({
        key: `factor-${factor.key}`,
        label: t(
          FACTOR_LABEL_KEY[factor.key] ?? "estimated_supply_formula.uplift"
        ),
        value: `${formatPercent(factor.rate)}%`,
      });
    });
  } else {
    // 구성을 못 받았으면(구버전 서버) 합쳐진 계수 하나로 보여준다.
    rows.push({
      key: "uplift",
      label: t("estimated_supply_formula.uplift"),
      value: `${formatPercent(breakdown.uplift)}%`,
    });
  }
  rows.push({
    key: "rate",
    label: t("estimated_supply_formula.rate"),
    value: `× ${formatRate(breakdown.rate)}`,
  });

  return (
    <Flex flexDirection={"column"} gap={"4px"} padding={"4px 2px"} minWidth={"220px"}>
      <Text fontSize={"12px"} fontWeight={700}>
        {t("estimated_landed_cost")}
      </Text>
      {rows.map((row) => (
        <Flex key={row.key} justifyContent={"space-between"} gap={"16px"} fontSize={"12px"}>
          <Text opacity={0.8}>{row.label}</Text>
          <Text whiteSpace={"nowrap"}>{row.value}</Text>
        </Flex>
      ))}
      <Flex
        justifyContent={"space-between"}
        gap={"16px"}
        fontSize={"12px"}
        fontWeight={700}
        borderTop={"0.5px solid rgba(255, 255, 255, 0.4)"}
        paddingTop={"4px"}
      >
        <Text>{t("estimated_supply_formula.rounding")}</Text>
        <Text whiteSpace={"nowrap"}>
          {`= ${tCurrencyString(breakdown.value, { type: "SYMBOL", code: "KRW" })}`}
        </Text>
      </Flex>
    </Flex>
  );
}

interface TooltipProps {
  // 계산할 수 없는 상품(공급가·환율 없음)은 null — 그때는 툴팁 없이 자식만 그린다.
  breakdown: EstimatedSupplyBreakdown | null;
  children: React.ReactElement;
}

/** 예상 매입가 값에 올리면 계산식을 보여준다. 그리드 셀과 카드 보기가 같이 쓴다. */
export default function EstimatedSupplyTooltip({
  breakdown,
  children,
}: TooltipProps) {
  if (!breakdown) {
    return children;
  }
  return (
    <Tooltip
      label={<EstimatedSupplyFormula breakdown={breakdown} />}
      placement={"top"}
      hasArrow={true}
      openDelay={150}
    >
      {children}
    </Tooltip>
  );
}
